import { showDateOnly, replaceIf } from "../../Common/Utility";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

export const PDF_HISTORY_EXPORT_HEADERS = {
  studentName: "Schüler/in",
  regionName: "Region",
  branchName: "Abrechnungstelle",
  pdfDate: "PDF-Datum",
  period: "Zeitraum",
  pdfFileName: "PDF-Datei",
};

const formatDate = (value) => {
  if (!value) return "";
  return showDateOnly(value);
};

const formatPeriod = (value) => {
  if (!value) return "";
  return replaceIf(value, "-", "--");
};

export const formatPdfHistoryRowData = (row) => {
  return [
    row.studentName || "",
    row.regionName || "",
    row.branchName || "",
    formatDate(row.pdfDate),
    formatPeriod(row.period),
    row.pdfFileName || "",
  ];
};

export const exportPdfHistoryToCsv = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return "";

  const headers = Object.values(PDF_HISTORY_EXPORT_HEADERS);
  const csvRows = [headers.join(";")];

  rows.forEach((row) => {
    const rowData = formatPdfHistoryRowData(row);
    csvRows.push(rowData.join(";"));
  });

  return csvRows.join("\n");
};

export const downloadPdfHistoryCsv = (
  csvString,
  filename = "PDF-Dateien.csv"
) => {
  if (!csvString) return;

  const blob = new Blob(["\ufeff" + csvString], {
    type: "text/csv;charset=utf-8;",
  });
  const link = document.createElement("a");

  try {
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url); // Clean up the URL object
  } catch (error) {
    console.error("Error downloading CSV:", error);
  }
};

export const handlePdfHistoryExport = (rows, format = "csv") => {
  if (!Array.isArray(rows)) {
    console.error("Invalid input data for export");
    return;
  }

  try {
    switch (format.toLowerCase()) {
      case "pdf":
        exportPdfHistoryToPdf(rows);
        break;
      case "csv":
      default:
        const csvString = exportPdfHistoryToCsv(rows);
        downloadPdfHistoryCsv(csvString);
    }
  } catch (error) {
    console.error("Error during export:", error);
  }
};

const calculatePdfHistoryColumnWidths = (headers, pageWidth) => {
  if (!headers) return [];

  // A3 landscape usable width (420mm - margins)
  const availableWidth = pageWidth - 28; // 28mm for left and right margins

  // Define column width ratios (total should be 100)
  const columnRatios = {
    0: 20, // Schüler/in
    1: 15, // Region
    2: 20, // Abrechnungstelle
    3: 12, // PDF-Datum
    4: 15, // Zeitraum
    5: 18, // PDF-Datei
  };

  // Calculate actual widths based on ratios and available width
  return headers.map((_, index) => {
    const ratio = columnRatios[index] || 10; // default ratio if not specified
    return (availableWidth * ratio) / 100;
  });
};

export const exportPdfHistoryToPdf = (rows, title = "PDF-Dateien") => {
  if (!Array.isArray(rows) || rows.length === 0) return;

  const headers = Object.values(PDF_HISTORY_EXPORT_HEADERS);
  const formattedRows = rows.map((row) => formatPdfHistoryRowData(row));

  try {
    // Create PDF document in A3 landscape
    const pdf = new jsPDF("l", "mm", "a3");
    const pageWidth = pdf.internal.pageSize.width;

    // Add title
    pdf.setFontSize(16);
    pdf.text(title, 14, 20);

    // Add table
    const columnWidths = calculatePdfHistoryColumnWidths(headers, pageWidth);

    // Use autoTable directly with optimized settings
    autoTable(pdf, {
      head: [headers],
      body: formattedRows,
      startY: 30,
      theme: "grid",
      columnStyles: headers.reduce((styles, header, index) => {
        styles[index] = {
          cellWidth: columnWidths[index],
          halign: getPdfHistoryColumnAlignment(index),
          minCellHeight: 8,
          cellPadding: { left: 2, right: 2, top: 2, bottom: 2 },
          overflow: "linebreak",
        };
        return styles;
      }, {}),
      styles: {
        fontSize: 8,
        overflow: "linebreak",
        font: "helvetica",
        minCellHeight: 8,
        valign: "middle",
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontSize: 9,
        fontStyle: "bold",
        halign: "center",
        minCellHeight: 10,
        valign: "middle",
      },
      bodyStyles: {
        textColor: [0, 0, 0],
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245],
      },
      margin: { top: 30, right: 14, bottom: 20, left: 14 },
      didDrawPage: (data) => {
        // Add page number at the bottom
        pdf.setFontSize(8);
        pdf.text(
          `Seite ${data.pageNumber} von ${pdf.getNumberOfPages()}`,
          pdf.internal.pageSize.width - 20,
          pdf.internal.pageSize.height - 10,
          { align: "right" }
        );

        pdf.text(``, 14, pdf.internal.pageSize.height - 10);
      },
    });

    // Save the PDF
    pdf.save("PDF-Dateien.pdf");
  } catch (error) {
    console.error("Error generating PDF:", error);
  }
};

// Helper function to determine column alignment
const getPdfHistoryColumnAlignment = (columnIndex) => {
  // Center align date columns
  if (columnIndex === 3) return "center";
  // Left align the rest
  return "left";
};
